import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Card, Flex, Heading, LinkBox, LinkOverlay } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { trackEvent } from '~/features/analytics/track-event';
import { getDefaultCatalogCarouselItems } from '~/features/experiments/ab_plp_redesign_2025/utils/get-default-catalog-carousel-items';
import { useActivePromoCampaign } from '~/features/promotions/hooks/use-active-promo-campaign';
import { useI18n } from '~/hooks/use-i18n';

import { CatalogSearchHeroCarousel } from './catalog-search-hero-carousel';

export function CatalogSearchHeroSection() {
  const { t } = useI18n();
  const heroTitle = t('catalog.hero.carousel.title');
  const { campaign, campaignContext, isGateReady, resolved, slideKey } = useActivePromoCampaign();

  const promoSlide =
    campaign?.surfaces.catalog && resolved != null
      ? campaign.surfaces.catalog({
          ...campaignContext,
          imagePriority: true,
          resolved,
        })
      : undefined;

  const replacedCarouselKeys = promoSlide ? campaign?.replacesEvergreenKeys?.catalog ?? [] : [];
  const allCarouselItems = getDefaultCatalogCarouselItems(t).filter((item) => !replacedCarouselKeys.includes(item.key));

  return (
    <Flex
      as='section'
      bgGradient='linear(273.36deg, #021031 28.34%, #00298C 100%)'
      minHeight={{ md: '180px' }}
      overflow='hidden'
      position='relative'
      padding={{
        base: '16px 0px',
        md: '24px 0px',
      }}
    >
      <Box
        inset={0}
        margin='0 auto'
        maxWidth='1250px'
        mixBlendMode='overlay'
        pointerEvents='none'
        position='absolute'
        display={{
          base: 'none',
          md: 'block',
        }}
      >
        <Image
          alt=''
          quality={75}
          src='/images/experiments/ab_plp_redesign_2025/udacity-u-bg.png'
          style={{
            objectFit: 'contain',
            objectPosition: 'right',
            transform: 'scaleX(-1)',
          }}
          fill
          priority
        />
      </Box>
      <ContentContainer
        contentMaxWidth='content-max-width-lg'
        display='flex'
        width='100%'
        alignItems={{
          md: 'center',
        }}
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
        gap={{
          base: '24px',
          md: '10px',
        }}
      >
        <Heading
          as='h1'
          color='white'
          flexShrink={0}
          fontWeight='600'
          whiteSpace={{ md: 'pre-wrap' }}
          fontSize={{
            base: '1.75rem',
            md: '2.25rem',
          }}
          letterSpacing={{
            base: '-0.84px',
            md: '-1.08px',
          }}
          lineHeight={{
            base: '34px',
            md: '44px',
          }}
          textAlign={{
            base: 'center',
            md: 'start',
          }}
          width={{
            md: '338px',
          }}
        >
          {heroTitle}
        </Heading>
        <Box
          width='100%'
          flex={{
            md: '1 1 748px',
          }}
          maxWidth={{
            md: '748px',
          }}
        >
          <CatalogSearchHeroCarousel
            isReady={isGateReady}
            items={allCarouselItems}
            promoSlide={promoSlide}
            promoSlideKey={slideKey ?? undefined}
            renderCard={({ item, index }, theme) => {
              const { cardBackgroundGradient, cardBorderColor, cardFrameBorderColor, titleEmphasisColor } = theme;
              const handleClick = () => trackEvent(item.eventPayload);
              const isClaudeEngineer = item.theme === 'claude-engineer';
              const hasCardFrame = Boolean(cardFrameBorderColor);
              const isEntireCardClickable = Boolean(item.entireCardClickable);
              const linkAriaLabel = `${item.title.replace(/\*\*/g, '').replace(/\n/g, ' ')}: ${item.ctaLabel}`;

              const heading = (
                <HeadingWithEmphasis
                  text={item.title}
                  emphasisProps={{
                    color: titleEmphasisColor,
                  }}
                  headingProps={{
                    as: 'p',
                    fontWeight: '600',
                    fontSize: {
                      base: '1.125rem',
                      md: '1.375rem',
                    },
                    lineHeight: '1.2',
                    noOfLines: {
                      base: 2,
                      md: 4,
                    },
                    whiteSpace: {
                      md: 'pre-wrap',
                    },
                    color: 'white',
                    position: 'relative',
                  }}
                />
              );

              const ctaButtonProps = {
                variant: 'solid' as const,
                colorScheme: 'blue' as const,
                width: 'fit-content' as const,
                position: 'relative' as const,
                zIndex: 1,
                size: isClaudeEngineer ? ({ base: 'sm', md: 'md' } as const) : ('md' as const),
                alignSelf: {
                  base: 'flex-start' as const,
                  md: 'unset' as const,
                },
                marginTop: item.ctaMarginTopAuto ? { base: 'auto', md: undefined } : undefined,
              };

              const card = (
                <Card
                  as={isEntireCardClickable ? 'div' : 'article'}
                  bgGradient={cardBackgroundGradient}
                  border={hasCardFrame ? 'none' : '1px solid'}
                  borderColor={hasCardFrame ? undefined : cardBorderColor}
                  borderRadius='8px'
                  display='flex'
                  flex={1}
                  flexWrap='nowrap'
                  isolation={isEntireCardClickable ? 'isolate' : undefined}
                  minHeight={{ md: '120px' }}
                  overflow='hidden'
                  position='relative'
                  alignItems={{
                    base: item.theme === 'claude-engineer' ? 'flex-start' : 'center',
                    md: 'center',
                  }}
                  flexDirection={{
                    base: 'column',
                    md: 'row',
                  }}
                  gap={{
                    base: isClaudeEngineer ? 2 : 4,
                    md: 4,
                  }}
                  height={{
                    base: '100%',
                    md: 'auto',
                  }}
                  justifyContent={{
                    md: 'space-between',
                  }}
                  padding={{
                    base: isClaudeEngineer ? 3 : 4,
                    md: 8,
                  }}
                >
                  <Box
                    inset={0}
                    position='absolute'
                    sx={{
                      img: {
                        objectFit: 'cover',
                        objectPosition: item.backgroundImageObjectPosition ?? 'bottom',
                        pointerEvents: 'none',
                      },
                    }}
                  >
                    <Image
                      alt=''
                      priority={index === 0}
                      quality={item.backgroundImageQuality ?? 75}
                      src={item.backgroundImage}
                      fill
                    />
                  </Box>
                  {item.overlay && <Box bgColor='blackAlpha.600' inset={0} position='absolute' />}
                  <Flex
                    flexDirection='column'
                    gap={item.logoImage ? { base: 1.5, md: 2.5 } : 0}
                    pointerEvents={isEntireCardClickable ? 'none' : undefined}
                    position='relative'
                    zIndex={isEntireCardClickable ? 1 : undefined}
                  >
                    {item.logoImage && (
                      <Box
                        height={{ base: '16px', md: '20px' }}
                        position='relative'
                        width={{ base: '74px', md: '93px' }}
                      >
                        <Image
                          alt='Claude'
                          src={item.logoImage}
                          style={{ objectFit: 'contain', objectPosition: 'left center' }}
                          fill
                        />
                      </Box>
                    )}
                    {isEntireCardClickable ? (
                      heading
                    ) : (
                      <LinkOverlay href={item.ctaUrl} onClick={handleClick}>
                        {heading}
                      </LinkOverlay>
                    )}
                  </Flex>
                  {isEntireCardClickable ? (
                    <Button {...ctaButtonProps} as='span' pointerEvents='none' tabIndex={-1} aria-hidden>
                      {item.ctaLabel}
                    </Button>
                  ) : (
                    <ButtonLink
                      linkProps={{ href: item.ctaUrl }}
                      buttonProps={{
                        ...ctaButtonProps,
                        onClick: handleClick,
                      }}
                    >
                      {item.ctaLabel}
                    </ButtonLink>
                  )}
                  {isEntireCardClickable ? (
                    <LinkOverlay
                      aria-label={linkAriaLabel}
                      as={NextLink}
                      href={item.ctaUrl}
                      inset={0}
                      position='absolute'
                      zIndex={2}
                      onClick={handleClick}
                    />
                  ) : null}
                </Card>
              );

              return (
                <Flex
                  border={hasCardFrame ? '1px solid' : { base: 'none', md: '6px solid' }}
                  borderColor={cardFrameBorderColor ?? 'hsla(0, 0%, 0%, 0.32)'}
                  borderRadius={hasCardFrame ? '8px' : { base: '8px', md: '12px' }}
                  maxWidth='656px'
                  mx='auto'
                  width='100%'
                  height={{
                    base: '135px',
                    md: 'full',
                  }}
                >
                  {isEntireCardClickable ? (
                    <LinkBox display='flex' flex={1} width='100%'>
                      {card}
                    </LinkBox>
                  ) : (
                    card
                  )}
                </Flex>
              );
            }}
          />
        </Box>
      </ContentContainer>
    </Flex>
  );
}
